import { AfterViewInit, Component, ElementRef, LOCALE_ID, OnDestroy, OnInit, inject } from '@angular/core';
import { DOCUMENT, DecimalPipe } from '@angular/common';
import { RouterModule } from '@angular/router';
import { DomSanitizer, Meta, SafeResourceUrl, Title } from '@angular/platform-browser';
import { links } from '../../shared/links';
import { initFeatureFx } from '../feature-fx.util';

/**
 * Kommerzielle SEO-Landingpage „Automatenladen eröffnen“ (/automatenladen).
 * Eigener Winkel: das GESCHÄFTSMODELL – ein 24/7-Laden ohne Personal, oft aus
 * mehreren Automaten kombiniert. Referenz: Automatenkiosk Langenargen
 * (2 marktbox + 8 weitere Automaten; marktbox am flexibelsten, ideal für
 * Bündel wie 6er-Bier). Blog: mit-einem-automaten-kiosk-voll-im-trend.
 */
@Component({
  selector: 'app-automatenladen-page',
  standalone: true,
  imports: [RouterModule, DecimalPipe],
  templateUrl: './automatenladen-page.component.html',
  styleUrls: ['../cluster-landing.shared.scss'],
})
export class AutomatenladenPageComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);
  private readonly document = inject(DOCUMENT);
  private readonly locale = inject(LOCALE_ID);
  private readonly host: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly sanitizer = inject(DomSanitizer);
  private observers: IntersectionObserver[] = [];
  private revealFallback?: ReturnType<typeof setTimeout>;

  readonly links = links;
  readonly blogSlug = 'mit-einem-automaten-kiosk-voll-im-trend';

  readonly liveUrl: SafeResourceUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
    'https://live.marktbox.de/automatenkiosk-langenargen/U-MB-00082'
  );

  readonly priceBuyFrom = 6800;
  readonly priceRentFrom = 300;

  readonly faqs: { q: string; a: string }[] = [
    {
      q: $localize`:@@alad.faq1.q:Was ist ein Automatenladen?`,
      a: $localize`:@@alad.faq1.a:Ein Automatenladen (oder Automatenkiosk) ist ein Verkaufsraum ganz ohne Personal: mehrere Automaten stehen zusammen und verkaufen rund um die Uhr. Die marktbox lässt sich dabei mit Automaten anderer Hersteller kombinieren.`,
    },
    {
      q: $localize`:@@alad.faq2.q:Warum die marktbox im Automatenladen?`,
      a: $localize`:@@alad.faq2.a:Weil sie am flexibelsten für unterschiedliche Waren ist – von Einzelartikeln bis zu Bündeln wie einem 6er-Bier. So deckt sie das ab, was klassische Snack- oder Getränkeautomaten nicht können.`,
    },
    {
      q: $localize`:@@alad.faq3.q:Lohnt sich ein Automatenladen?`,
      a: $localize`:@@alad.faq3.a:Ein Standort ohne Personal verkauft rund um die Uhr und spart laufende Personalkosten. Ob es sich rechnet, hängt von Standort, Sortiment und Frequenz ab – mehr dazu im Ratgeber.`,
    },
    {
      q: $localize`:@@alad.faq4.q:Automat kaufen oder mieten?`,
      a: $localize`:@@alad.faq4.a:Beides: ab 6.800 € kaufen oder ab 300 € pro Monat mieten. Für den Start eines Ladens lässt sich klein beginnen und später erweitern.`,
    },
  ];

  ngOnInit(): void {
    const canonical = this.canonicalUrl();
    const title = $localize`:@@alad.metaTitle:Automatenladen eröffnen – 24/7 ohne Personal | marktbox`;
    const description =
      $localize`:@@alad.metaDesc:Automatenladen eröffnen: 24/7-Verkauf ohne Personal. Die marktbox kombiniert sich flexibel mit weiteren Automaten. Ab 6.800 € oder 300 €/Monat.`;

    this.title.setTitle(title);
    this.meta.updateTag({ name: 'description', content: description });
    this.meta.updateTag({
      name: 'keywords',
      content: $localize`:@@alad.metaKeywords:Automatenladen, Automatenkiosk, 24/7 Laden ohne Personal, Verkaufsautomat Laden, marktbox`,
    });
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({ property: 'og:site_name', content: 'marktbox' });
    this.meta.updateTag({ property: 'og:title', content: title });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ property: 'og:url', content: canonical });
    this.meta.updateTag({
      property: 'og:image',
      content: 'https://www.marktbox.de/assets/headers/marktbox-00-1920.webp',
    });

    this.setCanonical(canonical);
    this.injectJsonLd('automatenladen-product-schema', {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: 'marktbox – Automat für den Automatenladen',
      description,
      image: 'https://www.marktbox.de/assets/headers/marktbox-00-1920.webp',
      brand: { '@type': 'Brand', name: 'marktbox' },
      category: 'Verkaufsautomat',
      offers: [
        {
          '@type': 'Offer',
          name: 'Kauf',
          priceCurrency: 'EUR',
          price: this.priceBuyFrom,
          availability: 'https://schema.org/InStock',
          itemCondition: 'https://schema.org/NewCondition',
          url: canonical,
          seller: { '@type': 'Organization', name: 'marktbox' },
        },
        {
          '@type': 'Offer',
          name: 'Miete',
          priceCurrency: 'EUR',
          price: this.priceRentFrom,
          availability: 'https://schema.org/InStock',
          url: canonical,
          seller: { '@type': 'Organization', name: 'marktbox' },
        },
      ],
    });
    this.injectJsonLd('automatenladen-faq-schema', {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: this.faqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    });
  }

  ngAfterViewInit(): void {
    this.observers = initFeatureFx(this.host.nativeElement);
    if (typeof window !== 'undefined' && typeof IntersectionObserver !== 'undefined') {
      this.revealFallback = setTimeout(() => {
        this.host.nativeElement
          .querySelectorAll('.reveal:not(.reveal-in)')
          .forEach((el) => el.classList.add('reveal-in'));
      }, 1200);
    }
  }

  ngOnDestroy(): void {
    this.observers.forEach((o) => o.disconnect());
    this.observers = [];
    if (this.revealFallback) clearTimeout(this.revealFallback);
  }

  scrollTo(event: Event, id: string): void {
    event.preventDefault();
    this.document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  private canonicalUrl(): string {
    const localePrefix = this.locale.startsWith('da') ? 'da' : this.locale.startsWith('nl') ? 'nl' : 'de';
    return `https://marktbox.de/${localePrefix}/automatenladen`;
  }

  private setCanonical(href: string): void {
    let link = this.document.querySelector<HTMLLinkElement>('link[rel=canonical]');
    if (!link) {
      link = this.document.createElement('link');
      link.rel = 'canonical';
      this.document.head.appendChild(link);
    }
    link.href = href;
  }

  private injectJsonLd(id: string, obj: object): void {
    this.document.getElementById(id)?.remove();
    const script = this.document.createElement('script');
    script.id = id;
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(obj);
    this.document.head.appendChild(script);
  }
}
